import styled from "@emotion/styled"
import { VisuallyHidden } from "@react-aria/visually-hidden"
import * as uniform from "@uniformdev/context-react"
import { waTriggerEvent } from "components/analytics/DigitalDataLayer"
import LanguageContainer from "components/footer/LanguageContainer"
import { Icon } from "components/icon/Icon"
import Bento from "components/nav-v2/bento/Bento"
import BentoController from "components/nav-v2/bento/Controller"
import Logo from "components/nav-v2/panes/Logo"
import { Action } from "components/nav-v2/Sub"
import { cssVars, useNav } from "components/nav-v2/Wrapper"
import { useConsole } from "contexts/Console"
import { useDictionary } from "contexts/Dictionary"
import { useLocale } from "contexts/Locale"
import { useNavigation } from "contexts/Navigation"
import { useUser } from "contexts/User"
import getMediaQuery, { breakpoint } from "css/breakpoints"
import { buttonReset } from "css/buttons"
import { body50, bold, legend100, legend110, normal } from "css/text"
import * as focusTrap from "focus-trap"
import { animate, useMotionValueEvent } from "framer-motion"
import useSSR from "hooks/useSSR"
import { match } from "path-to-regexp-8"
import { useId, useLayoutEffect, useRef } from "react"
import { Helmet } from "react-helmet-async"

const Pane = styled.nav`
  --dir: 1;
  --inset-inline: 1.8rem;
  [dir="rtl"] & {
    --dir: -1;
  }
  &.primary.main {
    pointer-events: none;
    content-visibility: hidden;

    [data-panes="primary"] & {
      pointer-events: auto;
      content-visibility: visible;
    }

    position: absolute;
    z-index: 0;
    inset-block: 0;
    inset-inline-start: 0;
    inset-inline-end: unset;
    width: 100vw;

    display: grid;
    grid-template-rows: 1fr clamp(5rem, 15vh, 10rem);

    transform: translate3d(
      calc(var(${cssVars.main.panes.primary.sub.expanded}) * -100% * var(--dir)),
      calc((1 - var(${cssVars.main.panes.primary.expanded})) * -100%),
      0
    );

    ${getMediaQuery("m", { max: true })} {
      /* first panel can't be seen on mobile */
      grid-template-rows: 1fr var(${cssVars.main.bar.height});

      &[data-sub="sub-0"] {
        transform: translate3d(0, calc((1 - var(${cssVars.main.panes.primary.expanded})) * -100% * var(--dir)), 0);
      }
    }

    @media (min-width: ${breakpoint.m}) {
      & {
        z-index: 10;
        inset-inline-end: unset;
        transform: translate3d(calc((1 - var(${cssVars.main.panes.primary.expanded})) * -100% * var(--dir)), 0, 0);
      }
    }

    @media (min-width: ${breakpoint.m}) {
      & {
        width: 384px;
      }
    }

    @media (min-width: 896px) {
      & {
        width: calc(100vw - 512px);
      }
    }

    ${getMediaQuery("l", { max: false })} {
      width: var(--pane-width);
    }

    background: rgb(var(--pure-white));
    & div.main.scroller {
      grid-column: 1/2;
      grid-row: 1/2;
      overflow: hidden;
      scrollbar-width: thin;
      display: flex;
      flex-direction: column;
      align-items: start;
      gap: 1.8rem;
      display: grid;
    }
    & div.main.content {
      grid-area: 1/1;
    }

    &[data-allow-scroll-self="true"] div.main.scroller {
      overflow: auto;
    }

    & > div:not(.main.scroller) {
      grid-column: 1/2;
      grid-row: 2/3;
      background: rgb(var(--pure-white));
      overflow: hidden;
      display: flex;
      justify-content: center;

      &:before {
        content: "";
        position: absolute;
        inset-block-end: 0;
        inset-inline: 0;
        background: linear-gradient(to top, rgb(var(--pure-white)), transparent);
        height: calc(var(${cssVars.main.bar.height}) + 1rem);
        width: 100%;
        margin-block-end: calc(var(${cssVars.main.bar.height}));
        pointer-events: none;
      }
      > svg {
        width: 100px;
        height: 50px;
      }
      ${getMediaQuery("m", { min: true })} {
        &:before {
          display: none;
        }
        > svg {
          width: 110px;
          height: 55px;
        }
      }
    }
  }

  &.primary.sub {
    --inset-inline: 1.8rem;
    pointer-events: none;
    content-visibility: hidden;

    position: absolute;
    z-index: 1;
    inset-block: 0;
    inset-inline-start: 100vw;
    inset-inline-end: unset;
    width: 100vw;

    @media (min-width: ${breakpoint.m}) {
      & {
        width: var(--pane-width);
      }
    }

    @media (min-width: ${breakpoint.m}) {
      & {
        --pane-width: calc(100vw - 384px);
        inset-inline-start: 384px;
      }
    }

    @media (min-width: 896px) {
      & {
        --pane-width: 512px;
        inset-inline-start: calc(100vw - var(--pane-width));
      }
    }

    ${getMediaQuery("l", { max: false })} {
      --pane-width: inherit;
      inset-inline-start: var(--pane-width);
    }

    ${getMediaQuery("m", { max: true })} {
      .pane.primary.main[data-sub-in="sub-0"] ~ &[data-sub="sub-0"] {
        pointer-events: none !important;
        content-visibility: hidden !important;
        transform: unset !important;
        display: none !important;
      }
    }

    .pane.primary.main[data-sub-in="sub-0"] .main.content button:nth-of-type(1) {
      pointer-events: none;
      cursor: default;
    }

    background: rgb(var(--pure-white) / var(--bg-opacity, 1));
    ${getMediaQuery("m", { max: false })} {
      background: rgb(var(--pure-white) / calc(var(--bg-opacity, 0) * 0.95));
    }

    display: grid;

    & div.sub.scroller {
      overflow-x: hidden;
      overflow-y: hidden;

      .pane.primary.main[data-allow-scroll="true"] ~ & {
        overflow-y: auto;
      }

      scrollbar-width: thin;
      display: grid;
    }

    & div.sub.content {
      width: calc(var(--pane-width) - var(--inset-inline) * 2);
      grid-column: 1/2;
      grid-row: 1/2;
      margin-block-start: 1.25rem;
      margin-inline: var(--inset-inline);
      position: relative;
      margin-block-end: 5.5rem;
      height: fit-content;

      ${getMediaQuery("m", { max: false })} {
        margin-block-start: min(12.89vh, 8.25rem);
        opacity: min(var(${cssVars.main.panes.primary.expanded}), var(--content-appear, 1), var(--content-opacity, 1));
      }

      @media (max-height: 680px) and (min-width: ${breakpoint.m}) {
        & {
          margin-block-start: min(5vh, 4rem);
        }
      }
    }
  }
`

const Items = styled.ul`
  display: flex;
  width: calc(100% - var(--inset-inline) * 2);
  flex-direction: column;
  align-items: start;
  margin: 0 var(--inset-inline);
  /* --inset-inline: 1.25rem; */
  margin-block-start: 0;
  margin-block-end: var(${cssVars.main.bar.height});

  > li {
    width: 100%;
  }

  > li:nth-of-type(2) {
    display: none;
  }

  .mobileOnly {
    width: 100%;
    display: block;
    margin-block-end: 1.5rem;
  }

  ${getMediaQuery("m", { min: true })} {
    width: 20rem;
    margin-block-start: min(12.89vh, 8.25rem);
    margin-inline: auto;
    margin-block-end: unset;

    li:nth-of-type(2) {
      display: flex;
    }

    .mobileOnly {
      display: none;
    }

    > li:nth-of-type(8) > button {
      margin-block-end: 0;
    }
  }

  & > *:last-child {
    margin-block-end: 0;
  }

  .shortcuts {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    > ul {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    margin-block: 3.75rem;

    @media (max-width: ${breakpoint.m}) {
      & {
        margin-block: calc(1.5rem - 0.88rem) 0;
        gap: min(1vh, 0.5rem);
      }
    }
  }

  @media (max-height: 680px) and (min-width: ${breakpoint.m}) {
    & {
      margin-block-start: min(5vh, 4rem);
    }
  }
`

const Button = styled.button`
  ${buttonReset}
  ${bold}
  font-size: 1.125rem;
  width: 100%;
  height: fit-content;
  display: flex;
  align-items: start;
  justify-content: space-between;
  margin-block-end: 0.88rem;

  text-align: start;

  @media (max-width: ${breakpoint.m}) {
    & {
      margin-block-end: min(1.2vh, 0.88rem);
    }
  }

  transition: color 300ms, fill 300ms;

  @media (any-hover: hover) and (any-pointer: fine) {
    &:hover {
      color: rgb(var(--green));
      fill: rgb(var(--green));
    }
  }

  > svg {
    display: block;
    width: 1rem;
    height: 1rem;
    color: rgb(var(--light-black));
    align-self: center;
  }

  ${getMediaQuery("m", { min: true })} {
    font-size: 1.18rem;
    margin-block-end: 1.25rem;

    &:nth-last-of-type(2) {
      margin-block-end: 0;
    }

    &:nth-of-type(1) {
      display: flex;
    }
    > svg:not(.lang-icon) {
      display: none;
    }
  }
`

const LangSwitch = styled(Button)`
  ${buttonReset}
  ${legend100}
  ${normal}
  color: rgb(var(--light-black));
  align-items: center;
  opacity: 1;
  justify-content: flex-start;
  transition: color 300ms, fill 300ms;
  width: fit-content;
  display: none;

  ${getMediaQuery("m", { min: true })} {
    display: flex;
    font-size: 14px;
  }

  svg {
    margin-inline-end: 0.5rem;
    transition: fill 300ms;
  }

  @media (any-hover: hover) and (any-pointer: fine) {
    &:hover {
      color: rgb(var(--green));

      & > svg {
        fill: rgb(var(--green));
      }
    }
  }
`

const Shortcut = styled.a`
  /* ${legend110} */
  ${normal}
  font-size: 1rem;
  text-decoration: none;
  color: currentColor;
  transition: color 300ms;

  @media (any-hover: hover) and (any-pointer: fine) {
    &:hover {
      color: rgb(var(--green));
    }
  }

  ${getMediaQuery("m", { max: true })} {
    &.active {
      color: rgb(var(--green));
    }
  }
`

const SubHeading = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  margin-block-end: calc(1.25rem - 0.5rem);

  > button {
    ${buttonReset}
    padding:0.5rem;
    margin-top: 0.25rem;
    margin-inline-start: -0.5rem;

    svg {
      transform: scale(-1);
    }

    [dir="rtl"] & {
      svg {
        transform: scale(1);
      }
    }
  }

  > span {
    position: absolute;
    inset-inline: 1.5rem;
    width: 0.03125rem;
    height: 50%;
    background: rgb(var(--light-black));
  }

  > p {
    /* ${body50} */
    /* font-size: 1.18rem; */
    margin-inline-start: 0.6rem;
    color: rgb(var(--green));
    ${bold}
  }

  ${getMediaQuery("m")} {
    display: none;
    margin-block-end: unset;
  }
`

const SubNavigation = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding-block-start: 1.87rem;

  ${getMediaQuery("m")} {
    padding-block-start: 3.75rem;
  }

  ${getMediaQuery("l")} {
    max-width: 22.5rem;
    margin-inline: auto;
    align-items: center;
  }
`

const SubPane = ({ i, switchBack, ...props }) => {
  const console = useConsole()
  const nav = useNav()
  const navigation = useNavigation()
  const isLanguages = props.label === "languages"
  const user = useUser()
  const locale = useLocale()
  const scores = uniform.useScores()
  const dictionary = useDictionary()

  const activePane = (props?.child_page_patterns ?? []).some(pattern => {
    return match(pattern)(navigation.slug)
  })

  const chooseBento = (bentos, bentos_p13n) => {
    let international = bentos.map(bento => bento.reference?.at?.(0))?.at?.(0)
    const variants = bentos_p13n
      .map(bento => {
        const component = bento.reference?.at?.(0)
        const criteria = bento.criteria?.name?.crit

        if (component && criteria) return { criteria, component }
        if (!criteria && component && !international) {
          international = component
        }
        return null
      })
      .filter(Boolean)

    for (const variant of variants) {
      const hit = variant.criteria.every(crit => {
        const { l: signal, op: operator, r: value } = crit

        switch (operator) {
          case "=":
            return +scores[signal] === +value
          default:
            return false
        }
      })

      if (hit) {
        return variant.component
      }
    }

    return international || null
  }

  const matchesCriteria = criteria => {
    if (!Array.isArray(criteria)) return false

    return criteria.every(crit => {
      const { l: signal, op: operator, r: value } = crit

      switch (operator) {
        case "=":
          return +scores[signal] === +value
        default:
          return false
      }
    })
  }

  const chooseActions = items => {
    const actions = items.map(item => item.action).filter(Boolean)
    const defaultActionsP13n = []
    const matchingActionsP13n = []

    for (const item of items) {
      const action_p13n = item.action_p13n
      if (!action_p13n) continue

      const criteria = action_p13n.criteria?.name?.crit
      if (!Array.isArray(criteria)) {
        defaultActionsP13n.push(action_p13n)
        continue
      }

      if (matchesCriteria(criteria)) matchingActionsP13n.push(action_p13n)
    }

    if (matchingActionsP13n.length) return matchingActionsP13n
    if (actions.length) return actions
    return defaultActionsP13n
  }

  const bentos = (props.items ?? []).map(item => item.bento).filter(Boolean)
  const bentos_p13n = (props.items ?? []).map(item => item.bento_p13n).filter(Boolean)
  const actions = chooseActions(props.items ?? [])
  const externalActions = (props.items ?? []).map(item => item?.external_action).filter(Boolean)
  const bento = chooseBento(bentos, bentos_p13n)

  const isLinkActive = item => item.href === navigation.slug

  const navigate = e => {
    e.preventDefault()
    navigation.navigate(e.currentTarget.getAttribute("href"))
  }

  const close = e => {
    e.preventDefault()
    nav.collapseAll()
  }

  const setLocaleAndNavigate = e => {
    e.preventDefault()
    user.locale = e.target.getAttribute("lang")
    requestAnimationFrame(() => {
      const targetLocale = locale.locales.find(locale => locale.codes.www === e.target.getAttribute("lang"))
      if (
        (targetLocale.codes.country && targetLocale.codes.country !== user.countryCode) ||
        (!targetLocale.codes.country && user.countryCode !== user.realCountryCode)
      )
        window.location.href = e.target.href
      else navigation.navigate(e.target.href, { preventLastLocation: true }, { replace: true })
    })
  }

  console.verbose("Nav:Panes:Primary:SubPane(%o)", { i, bento })
  return (
    <Pane
      key={props.label}
      aria-label={props.label}
      className={["pane primary sub", activePane && "active"].filter(Boolean).join(" ")}
      data-sub={`sub-${i}`}
      id={props.buttonId}
    >
      <div className='sub scroller'>
        <div className='sub content'>
          {isLanguages ? (
            <LanguageContainer isInFooter={false} isInNav={true} switchBack={switchBack} setLocaleAndNavigate={setLocaleAndNavigate} />
          ) : (
            <BentoController motion={nav.main.panes.primary.sub.motion.expanded} verticalIfDesktop={true}>
              <SubHeading>
                <button aria-label={dictionary.back()} type='button' className='back' onClick={switchBack}>
                  <Icon type='arrowBack' />
                </button>
                <span aria-hidden='true' tabIndex='-1' />
                <p>{props.label}</p>
              </SubHeading>
              {bento && <Bento {...bento} key={bento.uid} />}
              {actions.length > 0 || externalActions.length > 0 ? (
                <SubNavigation>
                  {actions.map(({ criteria, ...link }) => {
                    return (
                      <Action
                        key={link.label}
                        className={isLinkActive(link) ? "active" : ""}
                        href={navigation.localize(link.href)}
                        onClick={e => (isLinkActive(link) ? close(e) : navigate(e))}
                      >
                        {link.label}
                      </Action>
                    )
                  })}
                  {externalActions.map(link => {
                    return (
                      <Action
                        onClick={() => {
                          waTriggerEvent({
                            eventName: "outlink",
                            eventType: "Click",
                            outlinkInfo: {
                              linkURL: link.href,
                              linkName: link.outlink_name,
                            },
                          })
                        }}
                        className='externalAction'
                        key={link.label}
                        href={link.href}
                        target='_blank'
                        rel='noopener noreferrer'
                        aria-label={link.aria_label ? link.aria_label : null}
                      >
                        {link.label}
                        <Icon type='externalLink' fill='currentColor' />
                      </Action>
                    )
                  })}
                </SubNavigation>
              ) : null}
            </BentoController>
          )}
        </div>
      </div>
    </Pane>
  )
}

const Primary = props => {
  const console = useConsole()
  const nav = useNav()
  const rmain = useRef(null)
  const dictionary = useDictionary()
  const locale = useLocale()
  const navigation = useNavigation()
  const user = useUser()
  const scores = uniform.useScores()
  const ssr = useSSR()

  const setSectionTabIndex = i => {
    rmain.current?.querySelectorAll(".main.content button[data-section]").forEach((btn, idx) => {
      btn.tabIndex = idx === i ? -1 : 0
    })
  }

  const setSectionAriaExpanded = i => {
    rmain.current?.querySelectorAll(".main.content button[data-section]").forEach((btn, idx) => {
      btn.setAttribute("aria-expanded", idx === i ? "true" : "false")
    })
  }

  useMotionValueEvent(nav.main.panes.motion.expanded, "change", latest => {
    const previous = nav.main.panes.motion.expanded.getPrevious()
    const currDirection = rmain.current.dataset.direction

    if (latest >= previous && currDirection !== "up") rmain.current.dataset.direction = "up"
    if (latest > 0 && latest < previous && currDirection !== "down") rmain.current.dataset.direction = "down"
    if (latest === 0) rmain.current.dataset.direction = ""
  })

  useMotionValueEvent(nav.main.panes.primary.motion.expanded, "change", latest => {
    const previous = nav.main.panes.primary.motion.expanded.getPrevious()
    if (latest === 0) {
      rmain.current.dataset.subIn = "sub-0"
      rmain.current.querySelector(".content").scrollTop = 0

      for (const sub of rmain.current.parentElement.querySelectorAll(`.pane.sub .scroller`)) {
        sub.scrollTo({ top: 0, behavior: "instant" })
      }

      setSectionTabIndex(0)
      setSectionAriaExpanded(0)
    }

    const threshold = 0.66
    if (!previous && latest < threshold) {
      rmain.current.parentElement.style.setProperty("--content-appear", 0)
    }

    if (previous === 1) {
      rmain.current.dataset.allowScrollSelf = false
    }

    if (latest === 1) {
      rmain.current.dataset.allowScrollSelf = true
    }

    if (previous <= threshold && latest > threshold) {
      animate(0, 1, {
        ease: "easeOut",
        onUpdate: t => {
          if (t !== 1) rmain.current.parentElement.style.setProperty("--content-appear", t)
        },
      })
    }
  })

  useMotionValueEvent(nav.main.panes.primary.sub.motion.expanded, "change", latest => {
    const previous = nav.main.panes.primary.sub.motion.expanded.getPrevious()
    if (latest === 0) {
      rmain.current.dataset.subIn = "sub-0"
      setSectionTabIndex(0)
      setSectionAriaExpanded(0)
    }

    if (previous === 1 && latest < 1) {
      rmain.current.dataset.allowScroll = false
    }

    if (latest === 0) {
      rmain.current.dataset.allowScroll = true
    }

    if (latest === 1) {
      rmain.current.dataset.allowScroll = true
      rmain.current.dataset.subOut = ""
    }
  })

  useLayoutEffect(() => {
    const controller = new AbortController()

    nav.addEventListener(
      "toggle:languages",
      () => {
        switchPane(subPanes.length - 1)()
      },
      { signal: controller.signal }
    )

    return () => controller.abort()
  })

  useLayoutEffect(function focusTrapMgmt() {
    const controller = new AbortController()
    const refMQL = global.matchMedia(`(min-width: ${breakpoint.m})`)
    let trapTimeout
    let trap = null

    const onconditionschange = e => {
      try {
        const bar = props.rmainbar.current
        const close = props.rclose.current
        const pane = rmain.current
        const activeSubPane = pane.parentNode.querySelector(`.pane.primary.sub[data-sub='${pane.dataset.subIn}']`)

        if (trap) {
          trap.deactivate()
          trap = null
        }

        const desktop = refMQL.matches
        let initialFocus
        let stack
        switch (e.type) {
          case "change": {
            initialFocus = desktop ? close.querySelector("button") : bar.querySelector(".burger")

            if (desktop && (pane.contains(document.activeElement) || activeSubPane.contains(document.activeElement))) {
              initialFocus = document.activeElement
            }

            if (!desktop && activeSubPane.dataset.sub !== "sub-0" && activeSubPane.contains(document.activeElement)) {
              initialFocus = document.activeElement
            }
            if (!desktop && activeSubPane.dataset.sub === "sub-0" && pane.contains(document.activeElement)) {
              initialFocus = document.activeElement
            }

            stack = desktop ? [pane, activeSubPane, close] : activeSubPane.dataset.sub !== "sub-0" ? [activeSubPane, bar] : [pane, bar]
            break
          }
          case "primary:switch:back": {
            const subIndex = Number.parseInt(pane.dataset.subIn.replace("sub-", ""), 10)
            const sectionButtons = pane.querySelectorAll(".main.content button[data-section]")
            initialFocus = sectionButtons[subIndex] || bar.querySelector(".burger")

            stack = desktop ? [pane, activeSubPane, close] : [pane, bar]
            break
          }
          case "primary:switch": {
            initialFocus = desktop
              ? activeSubPane.querySelector("button:not(.back), a")
              : activeSubPane.querySelector("button.back") ?? bar.querySelector("button.back") ?? bar.querySelector(".burger")

            stack = desktop ? [pane, activeSubPane, close] : activeSubPane.dataset.sub !== "sub-0" ? [activeSubPane, bar] : [pane, bar]
            break
          }
          // case "init":
          default: {
            initialFocus = desktop ? close.querySelector("button") : bar.querySelector(".burger")

            stack = desktop ? [pane, activeSubPane, close] : [pane, bar]
            break
          }
        }

        trap = focusTrap.createFocusTrap(stack, {
          allowOutsideClick: true,
          initialFocus,
          isolateSubtrees: "aria-hidden",
          returnFocusOnDeactivate: false,
        })

        clearTimeout(trapTimeout)
        trapTimeout = setTimeout(
          () => {
            trap.activate()
          },
          e.type !== "init" ? 300 : 4
        )
      } catch (error) {
        console.error(error)
      }
    }

    const onprimaryexpandedchange = latest => {
      if (trap && latest === 0) {
        trap.deactivate()
        document.activeElement.blur()
        trap = null
        nav.__internals.dispatchEvent(new Event("focus:trap:deactivate:primary"))
      } else if (!trap && latest > 0) {
        refMQL.addEventListener("change", onconditionschange, { signal: controller.signal })
        onconditionschange({ type: "init" })
        nav.addEventListener("primary:switch", onconditionschange, { signal: controller.signal })
        nav.addEventListener("primary:switch:back", onconditionschange, { signal: controller.signal })
      }
    }

    const off = nav.main.panes.primary.motion.expanded.on("change", onprimaryexpandedchange)
    onprimaryexpandedchange(nav.main.panes.primary.motion.expanded.get())
    return () => {
      clearTimeout(trapTimeout)
      controller.abort()
      if (trap) trap.deactivate()
      off()
    }
  })

  const chooseBento = (bentos, bentos_p13n) => {
    let international = bentos.map(bento => bento.reference?.at?.(0))?.at?.(0)
    const variants = bentos_p13n
      .map(bento => {
        const component = bento.reference?.at?.(0)
        const criteria = bento.criteria?.name?.crit

        if (component && criteria) return { criteria, component }
        if (!criteria && component && !international) {
          international = component
        }
        return null
      })
      .filter(Boolean)

    for (const variant of variants) {
      const hit = variant.criteria.every(crit => {
        const { l: signal, op: operator, r: value } = crit

        switch (operator) {
          case "=":
            return +scores[signal] === +value
          default:
            return false
        }
      })

      if (hit) {
        return variant.component
      }
    }

    return international || null
  }

  const uid = useId()

  const sections = props.menu.map(item => item.section).filter(Boolean)
  const sectionIds = sections.map((_, i) => `${uid}-section-${i}`)
  const subPanes = [...sections, { label: "languages" }]
  const shortcuts = props.menu.map(item => item.shortcut).filter(Boolean)
  const featuredBentos = (subPanes.at(0)?.items ?? []).map(item => item.bento).filter(Boolean)
  const featuredBentosP13n = (subPanes.at(0)?.items ?? []).map(item => item.bento_p13n).filter(Boolean)
  const featuredBento = chooseBento(featuredBentos, featuredBentosP13n)

  const switchPane = i => () => {
    /** prevent opening the first sub-pane on mobile */
    const previous = rmain.current.dataset.subIn
    const mql = global.matchMedia(`(max-width: ${breakpoint.m})`)
    if ((mql.matches && i === 0) || previous === `sub-${i}`) {
      return
    }

    nav.main.panes.primary.sub.motion.expanded.set(0)
    Promise.all([
      animate(
        nav.main.panes.primary.sub.languages.motion.expanded,
        i === subPanes.length - 1 ? 1 : 0,
        global.matchMedia(`(min-width: ${breakpoint.m})`).matches ? { duration: 0.6, ease: "easeOut" } : {}
      ),
      animate(
        nav.main.panes.primary.sub.motion.expanded,
        1,
        global.matchMedia(`(min-width: ${breakpoint.m})`).matches ? { duration: 0.6, ease: "easeOut" } : {}
      ),
    ]).then(() => {
      const out = rmain.current.parentElement.querySelector(`.pane.sub[data-sub='${previous}'] > .scroller`)
      out.scrollTo({ top: 0, behavior: "instant" })
    })

    rmain.current.dataset.subIn = `sub-${i}`
    rmain.current.dataset.subOut = previous
    setSectionTabIndex(i)
    setSectionAriaExpanded(i)
    nav.__internals.dispatchEvent(new Event("primary:switch"))

    console.verbose("Nav:Panes:Primary:switch(%i:%s)", i, subPanes[i].label)
  }

  const switchBack = () => {
    Promise.all([animate(nav.main.panes.primary.sub.motion.expanded, 0), animate(nav.main.panes.primary.sub.languages.motion.expanded, 0)])
    setSectionTabIndex(0)
    nav.__internals.dispatchEvent(new Event("primary:switch:back"))
  }

  useLayoutEffect(() => {
    const controller = new AbortController()
    nav.addEventListener("toggle:languages:back", switchBack, { signal: controller.signal })

    return () => {
      controller.abort()
    }
  })

  const isLinkActive = item => item.href === navigation.slug
  const navigate = e => {
    e.preventDefault()
    navigation.navigate(e.currentTarget.getAttribute("href"))
  }
  const close = e => {
    e.preventDefault()
    nav.collapseAll()
  }

  useLayoutEffect(function setDefaultActivePane() {
    const main = rmain.current
    const wrapper = main.parentElement
    const panes = Object.values(wrapper.querySelectorAll(".pane.primary.sub"))
    panes.pop() // remove languages pane
    let activePane

    for (const pane of panes) {
      if (pane.classList.contains("active")) {
        activePane = pane
        break
      }
    }

    if (activePane) {
      main.dataset.subDefault = activePane.dataset.sub
    }
  })
  let subPanesCSS = ``
  for (const [i] of subPanes.entries()) {
    subPanesCSS += `
			.pane.primary.main[data-sub-in="sub-${i}"] ~ .pane.primary.sub[data-sub="sub-${i}"] {
				pointer-events: auto;
				content-visibility: visible;
				transform: translate3d(calc(var(${cssVars.main.panes.primary.sub.expanded}) * -100% * var(--dir)), 0, 0);
			}

      @media (max-width: ${breakpoint.m}) {
        .pane.primary.main[data-sub-in="sub-${i}"][data-direction="down"] ~ .pane.primary.sub[data-sub="sub-${i}"] {
          transform: translate3d(calc(-100% * var(--dir)), calc((1 - var(${cssVars.main.panes.primary.sub.expanded})) * -100%), 0);
        }
      }

      .pane.primary.main[data-sub-in="sub-${i}"] .main.content li[data-subpane-trigger-index="${i}"] > button {
        pointer-events: none;
        cursor: default;
        color: rgb(var(--green));
        fill: rgb(var(--green));
      }

      @media (width <= ${breakpoint.m}) {
          .pane.primary.main[data-sub-default="sub-${i}"] .main.content:not(:has([data-shortcut] .active)) li[data-subpane-trigger-index="${i}"] > button {
            color:rgb(var(--green));
          }
      }

			${getMediaQuery("m", { max: false })} {
				.pane.primary.main[data-sub-in="sub-${i}"] ~ .pane.primary.sub[data-sub="sub-${i}"],
				.pane.primary.main[data-sub-out="sub-${i}"] ~ .pane.primary.sub[data-sub="sub-${i}"] {
					transform: translate3d(calc((1 - var(${cssVars.main.panes.primary.expanded})) * -200% * var(--dir)), 0, 0);
				}

				.pane.primary.main[data-sub-in="sub-${i}"] ~ .pane.primary.sub[data-sub="sub-${i}"] {
					z-index: 2;
					--bg-opacity: round(down, var(${cssVars.main.panes.primary.sub.expanded}));
				}

				.pane.primary.main[data-sub-in="sub-${i}"]:not([data-sub-out*="sub"]) ~ .pane.primary.sub[data-sub="sub-${i}"] {
					--bg-opacity: 1;
				}

				.pane.primary.main[data-sub-in="sub-${i}"] ~ .pane.primary.sub[data-sub="sub-${i}"] .sub.content {
					--content-opacity: calc(clamp(0, var(${cssVars.main.panes.primary.sub.expanded}) * 2, 1));
					transform: translate3d(0, calc(2ex - var(${cssVars.main.panes.primary.sub.expanded}) * 2ex), 0);
				}

				.pane.primary.main[data-sub-in="sub-${i}"]:not([data-sub-out*="sub"]) ~ .pane.primary.sub[data-sub="sub-${i}"] .sub.content {
					--content-opacity: 1;
					transform: unset;
				}

				.pane.primary.main[data-sub-out="sub-${i}"] ~ .pane.primary.sub[data-sub="sub-${i}"] {
					z-index: 1;
					content-visibility: visible;
					--bg-opacity: round(up, var(${cssVars.main.panes.primary.sub.expanded}));
				}

				.pane.primary.main[data-sub-out="sub-${i}"] ~ .pane.primary.sub[data-sub="sub-${i}"] .sub.content {
					--content-opacity: calc(1 - clamp(0, var(${cssVars.main.panes.primary.sub.expanded}) * 2, 1));
				}

			}
		`
  }

  const isShortcutActive = href => {
    return navigation.slug.startsWith(href)
  }

  console.verbose("Nav:Panes:Primary(%o)", { locale, navigation, user, sections, shortcuts, subPanes, subPanesCSS })
  return (
    <>
      <Helmet>
        <style>{subPanesCSS}</style>
      </Helmet>
      <Pane
        ref={rmain}
        aria-label={dictionary.mainNavigation()}
        className='pane primary main'
        data-allow-scroll='true'
        data-sub-default='sub-0'
        data-sub-in='sub-0'
      >
        <div className='main scroller'>
          <div className='main content'>
            <Items aria-label='primary navigation'>
              {!ssr && featuredBento && (
                <li className='mobileOnly'>
                  <Bento {...featuredBento} />
                </li>
              )}
              {sections.map((section, i) => {
                return (
                  <li key={section.label} data-subpane-trigger-index={i}>
                    <Button
                      type='button'
                      aria-expanded={i === 0}
                      aria-controls={sectionIds[i]}
                      onClick={switchPane(i)}
                      data-section
                      tabIndex={i === 0 ? -1 : 0}
                    >
                      <span>{section.label}</span>
                      <Icon type='chevron' />
                    </Button>
                  </li>
                )
              })}
              {shortcuts ? (
                <li className='shortcuts'>
                  <ul aria-label={dictionary.shortcutLinks()}>
                    {shortcuts.map(shortcut => {
                      return (
                        <li data-shortcut key={shortcut.label}>
                          <Shortcut
                            href={navigation.localize(shortcut.href)}
                            className={isShortcutActive(shortcut.href) ? "active" : null}
                            onClick={e => (isLinkActive(shortcut) ? close(e) : navigate(e))}
                          >
                            {shortcut.label}
                          </Shortcut>
                        </li>
                      )
                    })}
                  </ul>
                </li>
              ) : null}
              <li>
                <LangSwitch onClick={switchPane(subPanes.length - 1)} aria-label={dictionary.ariaSwitchLanguage()}>
                  <Icon className='lang-icon' type='language' />
                  {locale?.current.label}
                  <VisuallyHidden>
                    <span id='changeLanguage'>{dictionary.changeLanguage()}</span>
                  </VisuallyHidden>
                </LangSwitch>
              </li>
            </Items>
          </div>
        </div>
        <div>
          <Logo />
        </div>
      </Pane>
      {subPanes.map((section, i) => {
        return <SubPane key={section.label} i={i} {...section} switchBack={switchBack} buttonId={sectionIds[i]} />
      })}
    </>
  )
}

export default Primary
